import type { TutorBilling, TutorBillingResponse } from '../../model/legal/interfaces'

import { useCommonStore } from '@shared/model'
import { getAxiosErrorMessage } from '@shared/api'
import { useAdminRoutes } from '@shared/lib/routes'
import { useToast } from '@shared/ui/toast'

import { useTutorLegalStore } from '../../model/legal/store'

interface TutorBillingApiOptions {
  tutorId: string | number
  onStart?: () => void
  onSuccess?: (billing: TutorBilling) => void
  onError?: (err: unknown) => void
  onFinish?: () => void
}

/** Guard against last-response-wins races: a late response for a tutor the
 *  admin has already navigated away from must not overwrite the store. */
const isRouteStillCurrent = (tutorId: string | number): boolean => {
  const currentId = useRouter().currentRoute.value.params.id
  return String(currentId) === String(tutorId)
}

/** Monotonic token: only the latest request may clear the shared loading flags,
 *  so a stale completion can't hide the loader while a newer request is pending. */
let requestSeq = 0

export const useTutorBillingApi = async (options: TutorBillingApiOptions): Promise<TutorBilling | null> => {
  const { $axios } = useNuxtApp()
  const toast = useToast()

  const url = useAdminRoutes('admin.tutors.billing', { tutor_id: options.tutorId })

  const tutorLegalStore = useTutorLegalStore()
  const commonStore = useCommonStore()

  const seq = ++requestSeq

  options.onStart?.()
  commonStore.setLoading(true)
  tutorLegalStore.setLoading(true)

  try {
    const { data } = await $axios.get<{ data: TutorBillingResponse }>(url!)
    const billing: TutorBilling | null = data.data?.billing || null

    if (!isRouteStillCurrent(options.tutorId)) return billing

    tutorLegalStore.setBilling(billing)

    if (billing) options.onSuccess?.(billing)

    return billing
  } catch (err: unknown) {
    console.error(err)
    if (isRouteStillCurrent(options.tutorId)) toast.error(getAxiosErrorMessage(err))
    options.onError?.(err)

    return null
  } finally {
    if (seq === requestSeq) {
      commonStore.setLoading(false)
      tutorLegalStore.setLoading(false)
    }
    options.onFinish?.()
  }
}
